/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import type { Query } from '@tanstack/react-query';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { idbPersister } from './persister';

/**
 * Reference-data query-key roots safe to persist for offline POS.
 * These are non-sensitive org catalog/config LISTS (confirmed key factories:
 * productKeys/categoryKeys/fxRateKeys/priceTierKeys/locationKeys/taxRateKeys — all `['venta', <token>]`).
 * Per-record sub-resources (e.g. `['venta','products','detail',<id>,'movements'|'price-history',...]`)
 * are intentionally excluded — see the `'detail'` check below.
 */
const REFERENCE_TOKENS = new Set([
  'products',
  'categories',
  'fx-rates',
  'price-tiers',
  'locations',
  'tax-rates',
]);

export function shouldPersistQuery(query: Pick<Query, 'state' | 'queryKey'>): boolean {
  if (query.state.status !== 'success') return false;
  const key = query.queryKey as unknown[];
  const [ns, token] = key;
  return (
    ns === 'venta' &&
    typeof token === 'string' &&
    REFERENCE_TOKENS.has(token) &&
    !key.includes('detail') // exclude per-record sub-resources (movements, price-history, …)
  );
}

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister: idbPersister,
  maxAge: 1000 * 60 * 60 * 24, // 24h
  buster: 'pwa-v1',
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => shouldPersistQuery(query),
    // Never persist mutations — a mutation paused offline would otherwise leak its payload to IndexedDB. Offline writes are out of scope (Phase 2).
    shouldDehydrateMutation: () => false,
  },
};
